import { ChartDepths, ChartLayersBase } from './mg-layers';
import Layer from 'ol/layer/Layer';
import TileLayer from 'ol/layer/WebGLTile';
import XYZ from 'ol/source/XYZ';
import { createXYZ } from 'ol/tilegrid';
import { StyleName } from '../../local_modules/voyage-style/src/voyage-style';
import { Tile, ImageTile } from 'ol';
import {Options} from 'ol/layer/BaseTile'
import { OSM, Source } from 'ol/source';
import { AuthService } from '../services/auth/auth-service';
import { apiCallTracker } from '../utils/api-call-tracker';

// EPSG:4326 - Unprojected
// EPSG:3395 - World Mercator
// EPSG:3857 - Pseudo/Web Mercator

const _PROJECTION = (globalThis as any)?.appenv?.projection;
const _ENCTILESERVER = (globalThis as any)?.appenv?.services?.encTileServer;
const _MAPTILESERVER = (globalThis as any)?.appenv?.services?.mapTileServer;
const isVesselOnCloud = (globalThis as any)?.appenv?.vesselOnCloud ?? false;
const scope = (globalThis as any)?.appenv?.scope ?? '';
const consumerKey = (globalThis as any)?.appenv?.consumerKey ?? '';
const xRequestor = (globalThis as any)?.appenv?.xRequestor ?? '';
const worldMap = (globalThis as any)?.appenv?.featureToggles?.worldMap ?? true;
const showMapViewOptions = (globalThis as any)?.appenv?.showMapViewOptions ?? false; // DEV ONLY

const isVessel = (globalThis as any)?.appenv?.vesselIMONumber !== undefined;

export class WBChartLayersImpl extends ChartLayersBase {
    constructor() {
        super();
    }
    get layers() {
        return this._layers;
    }
    setChartDepths(depths: ChartDepths) {
        this._depths = depths;
        this._layers.forEach(layer => {
            const source = layer.getSource();
            source!.refresh();
        })
    }
    setColorScheme(colorScheme: StyleName) {
        switch (colorScheme) {
            case StyleName.dusk:
                this._colorScheme = 'dusk';
                break;

            case StyleName.night:
                this._colorScheme = 'night';
                break;
            
            default:
                this._colorScheme = 'day';
                break;
        }

        localStorage.setItem('voyage-style', this._colorScheme);

        for (let layer of this._layers) {
            let src = layer.getSource()!;
            //At the time of writing this code, refresh() does not clear tile cache,
            // so changing theme doesnt take effect even after running refresh.
            // Clearing the tile cache manually is a workaround:
            // https://github.com/openlayers/openlayers/issues/13051
            //@ts-ignore
            layer.getRenderer().tileTextureCache_.clear();
            src.changed();
        }
    }


    async initialize(mapConfig: any) {
        let savedColorScheme:(string | null) = localStorage.getItem('voyage-style') || StyleName.default;
        
        if (!savedColorScheme || savedColorScheme === "default") {
            savedColorScheme = StyleName.default;
        }
        
        this._colorScheme = savedColorScheme;
        
        const authLoader = async (tile: Tile, url: string) => {
            try {
                const queryParams = new URLSearchParams({
                    safetyContour: this._depths.safetyContour.toString(),
                    safetyDepth: this._depths.safetyDepth.toString(),
                    shallowContour: this._depths.shallowContour.toString(),
                    deepContour: this._depths.deepContour.toString(),
                    beauty: 'true',
                    simplified: 'false',
                    levelOfDetail: '2',
                    coverage: 'false',
                    palette: this._colorScheme
                });

                let fullUrl = `${url}?${queryParams}`;
                if(isVesselOnCloud && showMapViewOptions){ // DEV ONLY - override query params to enable all features for testing
                    /*START.DEV_ONLY*/
                    fullUrl = url + `?safetyContour=${this._depths.safetyContour}&safetyDepth=${this._depths.safetyDepth}&shallowContour=${this._depths.shallowContour}&deepContour=${this._depths.deepContour}&beauty=true&simplified=${mapConfig.simplified}&levelOfDetail=${mapConfig.level}&coverage=false&palette=${this._colorScheme}`;
                    /*END.DEV_ONLY*/
                }

                // fetching tile with authorization header for cloud only
                if (isVesselOnCloud) {
                    const accessToken = await AuthService.getAccessToken(scope)
    
                    const response = await fetch(fullUrl, {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'consumer-key': consumerKey, 
                            'X-Requestor': xRequestor,
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    // Track all chart tile requests
                    const requestBytes = 0; // GET request with no body
                    const responseBytes = response.headers.get('content-length') 
                        ? Number(response.headers.get('content-length')) 
                        : (await response.clone().blob()).size;
                    
                    apiCallTracker.incrementCallCount(requestBytes, responseBytes, fullUrl);

                    const blob = await response.blob();
                    const imageUrl = URL.createObjectURL(blob);
                    const imageElement = (<ImageTile>tile).getImage() as HTMLImageElement;
                    imageElement.src = imageUrl;

                    // cleaning up the object URL once the image is loaded
                    imageElement.onload = () => {
                        URL.revokeObjectURL(imageUrl);
                    };
                } else {
                    // direct image assignment for non cloud
                    const imageElement = (<ImageTile>tile).getImage() as HTMLImageElement;
                    imageElement.src = fullUrl;
                }
            } catch (error) {
                console.error('Error loading tile:', error);
            }
        }

        const extent = <[number,number,number,number]>[-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244];
        const tileSize = [256, 256];
        const mapTileGrid1 = createXYZ({
            extent,
            tileSize,
            maxZoom: 4,
        });
        const mapTileGrid2 = createXYZ({
            extent,
            tileSize,
            minZoom: 5,
        });
        const encTileGrid = createXYZ({
            extent,
            tileSize,
        });

        // Custom loader for OpenStreetMap tiles to enable API tracking
        const osmLoader = async (tile: Tile, url: string) => {
            try {
                const response = await fetch(url);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Track OSM tile requests
                const requestBytes = 0; // GET request with no body
                const responseBytes = response.headers.get('content-length') 
                    ? Number(response.headers.get('content-length')) 
                    : (await response.clone().blob()).size;
                
                apiCallTracker.incrementCallCount(requestBytes, responseBytes, url);

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                const imageElement = (<ImageTile>tile).getImage() as HTMLImageElement;
                imageElement.src = imageUrl;

                // Clean up the object URL once the image is loaded
                imageElement.onload = () => {
                    URL.revokeObjectURL(imageUrl);
                };
            } catch (error) {
                console.error('Error loading OSM tile:', error);
                // Fallback to direct assignment on error
                const imageElement = (<ImageTile>tile).getImage() as HTMLImageElement;
                imageElement.src = url;
            }
        };

        const tilePixelRatio = 1;
        // Resolve tile server and projection at runtime to avoid issues when
        // module-level constants were evaluated before test shims ran.
        const runtimeProjection = _PROJECTION || (globalThis as any)?.appenv?.projection;
        const runtimeMapTileServer = _MAPTILESERVER?.baseurl ? _MAPTILESERVER : ( (globalThis as any)?.appenv?.services?.mapTileServer ?? { baseurl: '' } );
        const runtimeEncTileServer = _ENCTILESERVER?.baseurl ? _ENCTILESERVER : ( (globalThis as any)?.appenv?.services?.encTileServer ?? { baseurl: '' } );
        if (isVesselOnCloud && worldMap && !showMapViewOptions) {
            this._layers.push(
                new TileLayer(<Options<XYZ>>{
                    preload: 4,
                    source: new XYZ({
                        url: "https://tile.openstreetmap.de/{z}/{x}/{y}.png",
                        crossOrigin: 'anonymous',
                        tileLoadFunction: osmLoader,
                        attributions: [
                            '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                        ]
                    }),
                }),
            );
        } else {
            this._layers.push(
            new TileLayer(<Options<XYZ>>{
                preload: 4,
                source: new XYZ({
                    url: `${runtimeMapTileServer.baseurl}/map/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoader,
                    tileGrid: mapTileGrid1,
                    tilePixelRatio,
                    projection: runtimeProjection,
                }),
            }),
            new TileLayer(<Options<XYZ>>{
                minZoom: 4,
                source: new XYZ({
                    url: `${runtimeMapTileServer.baseurl}/map/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoader,
                    tileGrid: mapTileGrid2,
                    tilePixelRatio,
                    projection: runtimeProjection,
                }),
            }),
            new TileLayer(<Options<XYZ>>{
                source: new XYZ({
                    url: `${runtimeEncTileServer.baseurl}/enc/{z}/{x}/{-y}.png`,
                    tileLoadFunction: authLoader,
                    tileGrid: encTileGrid,
                    tilePixelRatio,
                    projection: runtimeProjection,
                }),
            }),
            );
        }    
        this._initialized = true;
    }

    zoomLevelChanged(zoom: number) {
    }


    private _initialized: boolean = false;
    private _layers: Layer<Source>[] = [];
    private _colorScheme: string = 'day';
    private _depths: ChartDepths = {
        safetyContour: 20,
        shallowContour: 15,
        deepContour: 30,
        safetyDepth: 20
    };
}